import React from "react";
import { ActionIcon, Group, Paper, Text } from "@mantine/core";
import {
  IconPencil,
  IconPin,
  IconPinFilled,
  IconTrash,
} from "@tabler/icons-react";
import moment from "moment";
import styles from "./NoteCard.module.css";

/**
 * A client note card: pinned (popup) notes are warm-tinted with a filled pin.
 * With onTogglePin the pin toggles; onEdit / onDelete add those actions
 * (appointment drawer Notes screen). Shared with the mobile detail sheet.
 */
export const NoteCard: React.FC<{
  text: string;
  author?: string | null;
  date?: string | null;
  pinned?: boolean;
  /** Authored by the client (client-visible, no staff author). */
  clientProvided?: boolean;
  onTogglePin?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  /** Show the whole note (the Notes screen) instead of 3 lines. */
  full?: boolean;
}> = ({
  text,
  author,
  date,
  pinned,
  clientProvided,
  onTogglePin,
  onEdit,
  onDelete,
  full = false,
}) => (
  <Paper
    withBorder
    radius="sm"
    p="xs"
    className={pinned ? `${styles.note} ${styles.pinned}` : styles.note}
  >
    <Group justify="space-between" wrap="nowrap" align="flex-start" gap={6}>
      <Text
        size="xs"
        lineClamp={full ? undefined : 3}
        className={pinned ? styles.pinnedText : undefined}
        style={{ flex: 1, whiteSpace: full ? "pre-wrap" : undefined }}
      >
        {text}
      </Text>
      <Group gap={2} wrap="nowrap">
        {onEdit && (
          <ActionIcon
            variant="subtle"
            size="sm"
            color="gray"
            aria-label="Edit note"
            onClick={onEdit}
          >
            <IconPencil size={15} />
          </ActionIcon>
        )}
        {onDelete && (
          <ActionIcon
            variant="subtle"
            size="sm"
            color="gray"
            aria-label="Delete note"
            onClick={onDelete}
          >
            <IconTrash size={15} />
          </ActionIcon>
        )}
        {onTogglePin ? (
          <ActionIcon
            variant="subtle"
            size="sm"
            color={pinned ? "var(--brand-burnt-orange)" : "gray"}
            aria-label={pinned ? "Unpin note" : "Pin note"}
            onClick={onTogglePin}
          >
            {pinned ? (
              <IconPinFilled size={15} className={styles.pinIcon} />
            ) : (
              <IconPin size={15} className={styles.pinIcon} />
            )}
          </ActionIcon>
        ) : (
          pinned && <IconPinFilled size={15} className={styles.pinIcon} />
        )}
      </Group>
    </Group>
    {(author || date) && (
      <Text
        size="10px"
        c={pinned ? undefined : "dimmed"}
        className={pinned ? styles.pinnedMeta : undefined}
        mt={2}
      >
        {author || (clientProvided ? "Provided by Client" : "Staff")}
        {date ? ` • ${moment(date).format("MMM DD, YYYY")}` : ""}
      </Text>
    )}
  </Paper>
);

export default NoteCard;
